/**
 * Workflow API Service
 * Handles communication with backend workflow endpoints
 */

import request from './request'
import { BASE_URL } from './baseUrl'
import type {
  WorkflowInstance,
  StartWorkflowRequest,
  ApproveStageRequest,
  RevisionRequest,
  StageContentUpdateRequest,
  WorkflowStageAssistantRequest,
  WorkflowStageAssistantResponse,
  WorkflowInputDataUpdateRequest,
  WorkflowProgress,
  FinalAudioOptionsResponse,
  FinalAudioWorkspaceGenerateRequest,
  FinalAudioWorkspaceGenerateResponse,
  FinalAudioWorkspaceMixRequest,
  FinalAudioWorkspaceMixResponse,
  ContentSeedRequest,
  ContentSeedResponse,
  ContentSeedConfirmRequest,
  ContentSeedConfirmResponse,
  ProductIntegrationRequest,
  ProductIntegrationResponse,
} from '@/types/workflow'

type StreamHandlers = {
  onMeta?: (payload: Omit<ContentSeedResponse, 'content'>) => void
  onReasoning?: (payload: { text: string }) => void
  onChunk?: (payload: { text: string }) => void
  onReplace?: (payload: { content: string }) => void
  onDone?: (payload: ContentSeedResponse) => void
}

type ProductStreamHandlers = {
  onReasoning?: (payload: { text: string }) => void
  onChunk?: (payload: { text: string }) => void
  onDone?: (payload: ProductIntegrationResponse) => void
}

export interface WorkflowPromptAssistantRequest {
  project_id: number
  field_label: string
  current_text?: string
  instruction: string
  stage?: string
  scene_number?: number
  asset_type?: string
  asset_name?: string
  purpose?: string
  text_model_id?: string
  operation_type?: string
}

async function yieldToBrowserFrame(): Promise<void> {
  if (typeof window === 'undefined') {
    return
  }

  await new Promise<void>((resolve) => {
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => resolve())
      return
    }
    window.setTimeout(resolve, 0)
  })
}

/**
 * Get workflow instance for a project
 */
export async function getWorkflow(projectId: number): Promise<WorkflowInstance> {
  return request.get<WorkflowInstance>(`/workflows/project/${projectId}`)
}

/**
 * Start or resume workflow execution
 */
export async function startWorkflow(params: StartWorkflowRequest): Promise<WorkflowInstance> {
  return request.post<WorkflowInstance>('/workflows/start', params, { timeout: 180000 })
}

export async function generateContentSeed(params: ContentSeedRequest): Promise<ContentSeedResponse> {
  return request.post<ContentSeedResponse>('/workflows/content/generate', params, { timeout: 120000 })
}

export async function streamGenerateContentSeed(params: ContentSeedRequest, handlers: StreamHandlers = {}): Promise<ContentSeedResponse> {
  const token = localStorage.getItem('token') || ''
  const response = await fetch(`${BASE_URL}/workflows/content/generate-stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(params),
  })

  if (!response.ok || !response.body) {
    let message = '内容生成失败，请稍后再试'
    try {
      const errorData = await response.json()
      message = errorData?.detail || errorData?.message || message
    } catch {
      message = response.statusText || message
    }
    throw new Error(message)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''
  let finalPayload: ContentSeedResponse | null = null

  const processEventBlock = async (block: string) => {
    const lines = block.split('\n')
    let eventName = 'message'
    const dataLines: string[] = []

    for (const line of lines) {
      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim()
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trim())
      }
    }

    if (!dataLines.length) {
      return
    }

    const payload = JSON.parse(dataLines.join('\n'))
    if (eventName === 'meta') {
      handlers.onMeta?.(payload)
      await yieldToBrowserFrame()
      return
    }
    if (eventName === 'reasoning') {
      handlers.onReasoning?.(payload)
      await yieldToBrowserFrame()
      return
    }
    if (eventName === 'chunk') {
      handlers.onChunk?.(payload)
      await yieldToBrowserFrame()
      return
    }
    if (eventName === 'replace') {
      handlers.onReplace?.(payload)
      await yieldToBrowserFrame()
      return
    }
    if (eventName === 'error') {
      throw new Error(payload?.message || '内容生成失败，请稍后再试')
    }
    if (eventName === 'done') {
      finalPayload = payload
      handlers.onDone?.(payload)
      await yieldToBrowserFrame()
    }
  }

  while (true) {
    const { value, done } = await reader.read()
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done })

    let separatorIndex = buffer.indexOf('\n\n')
    while (separatorIndex !== -1) {
      const eventBlock = buffer.slice(0, separatorIndex).trim()
      buffer = buffer.slice(separatorIndex + 2)
      if (eventBlock) {
        await processEventBlock(eventBlock)
      }
      separatorIndex = buffer.indexOf('\n\n')
    }

    if (done) {
      break
    }
  }

  if (!finalPayload) {
    throw new Error('内容生成已结束，但没有拿到完整结果')
  }

  return finalPayload
}

export async function confirmContentSeed(params: ContentSeedConfirmRequest): Promise<ContentSeedConfirmResponse> {
  return request.post<ContentSeedConfirmResponse>('/workflows/content/confirm', params, { timeout: 120000 })
}

export async function embedProductIntoContent(params: ProductIntegrationRequest): Promise<ProductIntegrationResponse> {
  return request.post<ProductIntegrationResponse>('/workflows/content/embed', params, { timeout: 120000 })
}

export async function streamEmbedProductIntoContent(
  params: ProductIntegrationRequest,
  handlers: ProductStreamHandlers = {}
): Promise<ProductIntegrationResponse> {
  const token = localStorage.getItem('token') || ''
  const response = await fetch(`${BASE_URL}/workflows/content/embed-stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(params),
  })

  if (!response.ok || !response.body) {
    let message = '内容整合失败，请稍后再试'
    try {
      const errorData = await response.json()
      message = errorData?.detail || errorData?.message || message
    } catch {
      message = response.statusText || message
    }
    throw new Error(message)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''
  let finalPayload: ProductIntegrationResponse | null = null

  const processEventBlock = async (block: string) => {
    const lines = block.split('\n')
    let eventName = 'message'
    const dataLines: string[] = []

    for (const line of lines) {
      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim()
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trim())
      }
    }

    if (!dataLines.length) {
      return
    }

    const payload = JSON.parse(dataLines.join('\n'))
    if (eventName === 'reasoning') {
      handlers.onReasoning?.(payload)
      await yieldToBrowserFrame()
      return
    }
    if (eventName === 'chunk') {
      handlers.onChunk?.(payload)
      await yieldToBrowserFrame()
      return
    }
    if (eventName === 'error') {
      throw new Error(payload?.message || '内容整合失败，请稍后再试')
    }
    if (eventName === 'done') {
      finalPayload = payload
      handlers.onDone?.(payload)
      await yieldToBrowserFrame()
    }
  }

  while (true) {
    const { value, done } = await reader.read()
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done })

    let separatorIndex = buffer.indexOf('\n\n')
    while (separatorIndex !== -1) {
      const eventBlock = buffer.slice(0, separatorIndex).trim()
      buffer = buffer.slice(separatorIndex + 2)
      if (eventBlock) {
        await processEventBlock(eventBlock)
      }
      separatorIndex = buffer.indexOf('\n\n')
    }

    if (done) {
      break
    }
  }

  if (!finalPayload) {
    throw new Error('内容整合已结束，但没有拿到完整结果')
  }

  return finalPayload
}

/**
 * Pause workflow execution
 */
export async function pauseWorkflow(workflowId: number): Promise<WorkflowInstance> {
  return request.post<WorkflowInstance>(`/workflows/${workflowId}/pause`)
}

/**
 * Resume a paused workflow
 */
export async function resumeWorkflow(workflowId: number): Promise<WorkflowInstance> {
  return request.post<WorkflowInstance>(`/workflows/${workflowId}/resume`, undefined, { timeout: 180000 })
}

/**
 * Approve a stage (script, storyboard, final)
 */
export async function approveStage(
  workflowId: number,
  params: ApproveStageRequest
): Promise<WorkflowInstance> {
  return request.post<WorkflowInstance>(`/workflows/${workflowId}/approve`, params, { timeout: 180000 })
}

/**
 * Request revision for a stage
 */
export async function requestRevision(
  workflowId: number,
  params: RevisionRequest
): Promise<WorkflowInstance> {
  return request.post<WorkflowInstance>(`/workflows/${workflowId}/revision`, params, { timeout: 180000 })
}

export async function updateStageContent(
  workflowId: number,
  params: StageContentUpdateRequest
): Promise<WorkflowInstance> {
  return request.post<WorkflowInstance>(`/workflows/${workflowId}/stage-content`, params)
}

export async function updateWorkflowInputData(
  workflowId: number,
  params: WorkflowInputDataUpdateRequest
): Promise<WorkflowInstance> {
  return request.post<WorkflowInstance>(`/workflows/${workflowId}/input-data`, params)
}

export async function clearStoryboardSceneImage(
  workflowId: number,
  sceneNumber: number,
): Promise<{ scene: Record<string, any>; workflow: WorkflowInstance }> {
  return request.post<{ scene: Record<string, any>; workflow: WorkflowInstance }>(
    `/workflows/${workflowId}/storyboard-scenes/${sceneNumber}/image/clear`
  )
}

export async function generateStoryboardSceneImage(
  workflowId: number,
  sceneNumber: number,
  params?: {
    prompt?: string
    original_prompt?: string
    image_model_id?: string
    reference_image_urls?: string[]
    referenced_assets?: {
      character_ids?: string[]
      scene_ids?: string[]
      prop_ids?: string[]
    }
  }
): Promise<{ scene: Record<string, any>; workflow: WorkflowInstance }> {
  return request.post<{ scene: Record<string, any>; workflow: WorkflowInstance }>(
    `/workflows/${workflowId}/storyboard-scenes/${sceneNumber}/image`,
    params || {},
    { timeout: 180000 }
  )
}

export async function generateStoryboardSceneVideo(
  workflowId: number,
  sceneNumber: number,
  params?: {
    prompt?: string
    duration?: number
    video_model_id?: string
    referenced_assets?: {
      character_ids?: string[]
      scene_ids?: string[]
      prop_ids?: string[]
    }
  }
): Promise<{ task: Record<string, any>; workflow: WorkflowInstance; already_running?: boolean }> {
  return request.post<{ task: Record<string, any>; workflow: WorkflowInstance; already_running?: boolean }>(
    `/workflows/${workflowId}/storyboard-scenes/${sceneNumber}/video`,
    params || {},
    { timeout: 720000 }
  )
}

export async function getStoryboardSceneVideoTaskStatus(
  workflowId: number,
  sceneNumber: number
): Promise<{ task: Record<string, any>; scene?: Record<string, any> | null; workflow: WorkflowInstance }> {
  return request.get<{ task: Record<string, any>; scene?: Record<string, any> | null; workflow: WorkflowInstance }>(
    `/workflows/${workflowId}/storyboard-scenes/${sceneNumber}/video-task`
  )
}

export async function optimizeWorkflowPrompt(
  params: WorkflowPromptAssistantRequest
): Promise<{ optimized_text: string }> {
  return request.post<{ optimized_text: string }>(
    '/workflows/prompt-assistant',
    params,
    { timeout: 120000 }
  )
}

export async function runStageAssistantTurn(
  params: WorkflowStageAssistantRequest
): Promise<WorkflowStageAssistantResponse> {
  return request.post<WorkflowStageAssistantResponse>(
    '/workflows/stage-assistant',
    params,
    { timeout: 120000 }
  )
}

export async function getFinalAudioOptions(
  workflowId: number,
  params?: { provider?: string },
): Promise<FinalAudioOptionsResponse> {
  return request.get<FinalAudioOptionsResponse>(`/workflows/${workflowId}/final-audio/options`, { params })
}

export async function generateFinalAudioTts(
  workflowId: number,
  params: FinalAudioWorkspaceGenerateRequest,
): Promise<FinalAudioWorkspaceGenerateResponse> {
  return request.post<FinalAudioWorkspaceGenerateResponse>(
    `/workflows/${workflowId}/final-audio/tts`,
    params,
    { timeout: 120000 },
  )
}

export async function mixFinalAudioIntoVideo(
  workflowId: number,
  params: FinalAudioWorkspaceMixRequest,
): Promise<FinalAudioWorkspaceMixResponse> {
  return request.post<FinalAudioWorkspaceMixResponse>(
    `/workflows/${workflowId}/final-audio/mix`,
    params,
    { timeout: 180000 },
  )
}

export async function generateConsistencyAssetImage(
  workflowId: number,
  assetType: 'character' | 'scene' | 'prop',
  assetId: string,
  params?: { prompt?: string }
): Promise<{ asset: Record<string, any>; workflow: WorkflowInstance }> {
  return request.post<{ asset: Record<string, any>; workflow: WorkflowInstance }>(
    `/workflows/${workflowId}/consistency-assets/${assetType}/${encodeURIComponent(assetId)}/image`,
    params || {},
    { timeout: 180000 }
  )
}

export async function selectStoryboardSceneImage(
  workflowId: number,
  sceneNumber: number,
  params: { image_url: string; source?: string }
): Promise<{ scene: Record<string, any>; workflow: WorkflowInstance }> {
  return request.post<{ scene: Record<string, any>; workflow: WorkflowInstance }>(
    `/workflows/${workflowId}/storyboard-scenes/${sceneNumber}/image/select`,
    params
  )
}

/**
 * Get workflow progress
 */
export async function getWorkflowProgress(workflowId: number): Promise<WorkflowProgress[]> {
  return request.get<WorkflowProgress[]>(`/workflows/${workflowId}/progress`)
}

/**
 * Get agent logs
 */
export async function getAgentLogs(
  workflowId: number,
  limit: number = 50
): Promise<any[]> {
  return request.get<any[]>(`/workflows/${workflowId}/logs`, { params: { limit } })
}

/**
 * WebSocket connection for real-time updates
 */
export function createWorkflowWebSocket(workflowId: number) {
  const token = localStorage.getItem('token') || ''
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const base = BASE_URL.startsWith('http')
    ? new URL(BASE_URL)
    : new URL(BASE_URL, window.location.origin)
  const wsUrl = `${protocol}//${base.host}${base.pathname.replace(/\/$/, '')}/workflows/ws/${workflowId}?token=${encodeURIComponent(token)}`

  return new WebSocket(wsUrl)
}
